/* Public WiseReviews widget -> CKL cards. No copied testimonials or private API. */
(() => {
  'use strict';
  const SELECTOR = '[data-ckl-home-trusted-reviews][data-ckl-review-source="wisereviews"]';
  const DESKTOP_MQ = '(min-width: 1000px)';
  const LIMIT = 3;
  if (window.CKLHomeLiveReviews) {
    window.CKLHomeLiveReviews.init(document);
    return;
  }

  const clean = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const readRating = (slide) => {
    // WiseReviews clips each filled SVG inside a percentage-width HTML span.
    // The path's immediate parent is an SVG <g>, not the clipping element.
    const overlays = [...new Set(Array.from(slide.querySelectorAll('.wr-star-symbol-filled'), (path) => path.closest('svg')?.parentElement).filter(Boolean))];
    if (overlays.length !== 5) return null;
    const widths = overlays.map((overlay) => {
      const value = overlay.style.width.trim();
      return /^\d+(?:\.\d+)?%$/.test(value) ? Number.parseFloat(value) : NaN;
    });
    if (widths.some((width) => !Number.isFinite(width) || width < 0 || width > 100)) return null;
    const rating = Math.round(widths.reduce((sum, width) => sum + width / 100, 0) * 10) / 10;
    return rating > 0 && rating <= 5 ? rating : null;
  };

  const readReviews = (source, readMoreLabel) => {
    const seen = new Set();
    const reviews = [];
    // Preserve the provider's featured-review order and omit carousel clones.
    for (const slide of source.querySelectorAll('.slick-slide:not(.slick-cloned)')) {
      const bodyNode = slide.querySelector('p.wise-mb-4');
      const author = clean(slide.querySelector('.wise-font-bold')?.textContent);
      if (!bodyNode || !author) continue;
      const copy = bodyNode.cloneNode(true);
      copy.querySelectorAll('button,script,style').forEach((node) => node.remove());
      let body = clean(copy.textContent);
      if (readMoreLabel && body.endsWith(readMoreLabel)) body = clean(body.slice(0, -readMoreLabel.length));
      if (!body) continue;
      const date = clean(slide.querySelector('p.wise-text-xs')?.textContent);
      const rating = readRating(slide);
      const key = JSON.stringify([author, body, date, rating]);
      if (seen.has(key)) continue;
      seen.add(key);
      reviews.push({ author, body, date, rating });
      if (reviews.length === LIMIT) break;
    }
    return reviews;
  };

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const buildStars = (rating) => {
    const stars = element('span', 'ckl-home-trusted-reviews__card-stars');
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `${rating} out of 5 stars`);
    for (let index = 0; index < 5; index += 1) {
      const star = element('span', 'ckl-live-review-star', '\u2605');
      star.style.setProperty('--ckl-star-fill', `${Math.max(0, Math.min(1, rating - index)) * 100}%`);
      star.setAttribute('aria-hidden', 'true');
      stars.appendChild(star);
    }
    return stars;
  };

  const buildCard = (review) => {
    const card = element('article', 'ckl-home-trusted-reviews__card');
    card.setAttribute('role', 'listitem');
    const head = element('div', 'ckl-home-trusted-reviews__card-head');
    const quoteMark = element('span', 'ckl-home-trusted-reviews__quote-mark', '\u201c');
    quoteMark.setAttribute('aria-hidden', 'true');
    head.appendChild(quoteMark);
    // Missing source ratings are omitted, never replaced with five stars.
    if (review.rating !== null) head.appendChild(buildStars(review.rating));
    const quote = element('p', 'ckl-home-trusted-reviews__quote', review.body);
    const footer = element('footer', 'ckl-home-trusted-reviews__reviewer');
    footer.appendChild(element('strong', '', review.author));
    if (review.date) footer.appendChild(element('time', '', review.date));
    card.append(head, quote, footer);
    return card;
  };

  const bindDots = (section) => {
    section._cklDotsAbort?.abort();
    section._cklDotsAbort = null;
    if (window.matchMedia(DESKTOP_MQ).matches) return;
    const track = section.querySelector('[data-ckl-reviews-track]');
    const cards = Array.from(section.querySelectorAll('[data-ckl-live-reviews] > article'));
    const dots = Array.from(section.querySelectorAll('[data-ckl-reviews-dots] > button'));
    if (!track || cards.length < 2 || !window.CKLHomeCarouselDots) return;
    window.CKLHomeCarouselDots.bindCarouselDots({
      root: section, track, dots, labelPrefix: 'Review',
      getTargets: () => cards.map((card) => Math.max(0, card.offsetLeft - cards[0].offsetLeft))
    });
  };

  const initSection = (section) => {
    if (section._cklLiveReviewsDestroy) return;
    const source = section.querySelector('[data-ckl-reviews-source]');
    const output = section.querySelector('[data-ckl-live-reviews]');
    const status = section.querySelector('[data-ckl-reviews-status]');
    const message = section.querySelector('[data-ckl-reviews-message]');
    const dotList = section.querySelector('[data-ckl-reviews-dots]');
    const track = section.querySelector('[data-ckl-reviews-track]');
    if (!source || !output || !status || !message || !dotList || !track) return;

    section.querySelectorAll('[data-ckl-review-total]').forEach((node) => {
      const count = Number(node.dataset.cklReviewTotal);
      if (Number.isSafeInteger(count) && count > 0) {
        try { node.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en').format(count); }
        catch { node.textContent = String(count); }
      }
    });

    let signature = '[]';
    let timer = 0;
    let destroyed = false;
    track.setAttribute('aria-busy', 'true');
    message.textContent = 'Loading customer reviews...';
    const fallbackTimer = window.setTimeout(() => {
      if (!output.hidden) return;
      track.setAttribute('aria-busy', 'false');
      message.textContent = 'Customer reviews are available on our reviews page.';
      section.dataset.reviewsState = 'unavailable';
    }, 12000);

    const render = () => {
      if (destroyed || !section.isConnected) return;
      const reviews = readReviews(source, clean(section.dataset.cklReadMore));
      const next = JSON.stringify(reviews);
      if (next === signature) return;
      signature = next;
      output.replaceChildren(...reviews.map(buildCard));
      output.hidden = reviews.length === 0;
      status.hidden = reviews.length > 0;
      section.classList.toggle('is-ready', reviews.length > 0);
      section.dataset.reviewCount = String(reviews.length);
      section.dataset.reviewsState = reviews.length ? 'ready' : 'unavailable';
      dotList.replaceChildren(...reviews.map((review, index) => {
        const dot = element('button', `ckl-home-trusted-reviews__dot${index === 0 ? ' is-active' : ''}`);
        dot.type = 'button';
        dot.setAttribute('aria-label', `Review ${index + 1}`);
        if (index === 0) dot.setAttribute('aria-current', 'true');
        return dot;
      }));
      dotList.hidden = reviews.length < 2;
      track.setAttribute('aria-busy', 'false');
      if (reviews.length) window.clearTimeout(fallbackTimer);
      else message.textContent = 'Customer reviews are available on our reviews page.';
      bindDots(section);
    };

    const observer = new MutationObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(render, 100);
    });
    observer.observe(source, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['style'] });
    const mq = window.matchMedia(DESKTOP_MQ);
    const onBreakpoint = () => bindDots(section);
    mq.addEventListener('change', onBreakpoint);
    section._cklLiveReviewsDestroy = () => {
      destroyed = true;
      observer.disconnect();
      window.clearTimeout(timer);
      window.clearTimeout(fallbackTimer);
      mq.removeEventListener('change', onBreakpoint);
      section._cklDotsAbort?.abort();
      section._cklDotsAbort = null;
      delete section._cklLiveReviewsDestroy;
    };
    render();
  };

  const init = (root = document) => {
    if (root.matches?.(SELECTOR)) initSection(root);
    root.querySelectorAll(SELECTOR).forEach(initSection);
  };
  window.CKLHomeLiveReviews = { init, version: 'native-wisereviews-v2' };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.(SELECTOR)) event.target._cklLiveReviewsDestroy?.();
    event.target.querySelectorAll(SELECTOR).forEach((section) => section._cklLiveReviewsDestroy?.());
  });
})();
